/**
 * Starter TextFilterControl — a controlled, presentational free-text filter
 * for ONE text field in the Portfolio Segment Builder. Pairs an operator
 * `Select` (8 string ops) with a value `TextInput` and a "Case sensitive"
 * `Switch`. The value input is hidden for the value-less ops (`is_blank` /
 * `is_not_blank`), which match purely on presence.
 *
 * For `regex`, the pattern is validated live against the SAME dialect the server
 * executes — Postgres POSIX ARE (F100). Beyond a `new RegExp` compile check and the
 * 200-char cap, constructs valid in JavaScript but unsupported by Postgres ARE (named
 * capture groups, named backreferences, Unicode property escapes) are flagged too, so
 * a pattern that would 500 at SQL is caught here instead. An inline error renders below
 * the field when it's invalid, too long, or outside the ARE subset — but typing is never
 * blocked.
 *
 * Pure presentation: it renders entirely from props and emits the full next
 * `{ op, value, caseSensitive }` shape through `onChange`. Built on the kit's
 * Mantine pass-throughs (Select / TextInput / Switch) so it inherits the
 * theme's focus ring + border palette; the inline error uses the Vintage
 * error token via a CSS module. All user-facing strings route through i18n
 * (`segment.text.*`).
 */
import type { ReactNode } from 'react';
import { Select, Switch, TextInput } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { isPosixAreUnsupportedRegex, SEGMENT_REGEX_MAX_LENGTH } from '@repo/shared/common';
import styles from './TextFilterControl.module.css';

export type TextFilterOp =
  | 'contains'
  | 'equals'
  | 'starts_with'
  | 'ends_with'
  | 'not_contains'
  | 'regex'
  | 'is_blank'
  | 'is_not_blank';

export interface TextFilterControlProps {
  readonly label: string;
  readonly op: TextFilterOp;
  readonly value: string;
  readonly caseSensitive: boolean;
  readonly onChange: (next: {
    op: TextFilterOp;
    value: string;
    caseSensitive: boolean;
  }) => void;
  readonly disabled?: boolean;
  /**
   * Embedded in a host surface (the SegmentPanel's white body): suppress the
   * internal field label so it doesn't repeat the FieldRow header's name (the
   * label is still used for the controls' aria-labels). This control has no card
   * chrome of its own, so there is nothing else to strip. Standalone (stories)
   * leave it off.
   */
  readonly embedded?: boolean;
}

/** Operator → its i18n key (under `segment.text.ops`), in display order. */
const OP_LABEL_KEYS: ReadonlyArray<{ value: TextFilterOp; labelKey: string }> = [
  { value: 'contains', labelKey: 'segment.text.ops.contains' },
  { value: 'equals', labelKey: 'segment.text.ops.equals' },
  { value: 'starts_with', labelKey: 'segment.text.ops.startsWith' },
  { value: 'ends_with', labelKey: 'segment.text.ops.endsWith' },
  { value: 'not_contains', labelKey: 'segment.text.ops.notContains' },
  { value: 'regex', labelKey: 'segment.text.ops.regex' },
  { value: 'is_blank', labelKey: 'segment.text.ops.isBlank' },
  { value: 'is_not_blank', labelKey: 'segment.text.ops.isNotBlank' },
];

/** Ops that match on presence alone — the value input is irrelevant. */
const VALUELESS_OPS: ReadonlySet<TextFilterOp> = new Set<TextFilterOp>([
  'is_blank',
  'is_not_blank',
]);

/**
 * Validate a regex pattern for live feedback in the SAME dialect Postgres runs (POSIX ARE, F100).
 * Returns the i18n args for the error message when the pattern is too long, fails to compile, or
 * uses a construct Postgres ARE cannot evaluate, else `null`. Never throws.
 */
function validateRegex(
  pattern: string,
): { key: string; args: Record<string, unknown> } | null {
  if (pattern.length > SEGMENT_REGEX_MAX_LENGTH) {
    return { key: 'segment.text.regexTooLong', args: { max: SEGMENT_REGEX_MAX_LENGTH } };
  }
  // Valid JavaScript, invalid Postgres ARE (named groups / backrefs / \p{…}) — would 500 at SQL.
  if (isPosixAreUnsupportedRegex(pattern)) {
    return { key: 'segment.text.regexUnsupported', args: {} };
  }
  try {
    void new RegExp(pattern);
    return null;
  } catch (err) {
    const detail = err instanceof Error ? err.message : String(err);
    return { key: 'segment.text.regexInvalid', args: { detail } };
  }
}

export function TextFilterControl({
  label,
  op,
  value,
  caseSensitive,
  onChange,
  disabled = false,
  embedded = false,
}: TextFilterControlProps): ReactNode {
  const { t } = useTranslation();
  const showValue = !VALUELESS_OPS.has(op);
  const regexError =
    op === 'regex' && value.length > 0 ? validateRegex(value) : null;

  return (
    <div
      className={[styles.root, embedded ? styles.embedded : undefined]
        .filter(Boolean)
        .join(' ')}
    >
      {embedded ? null : <span className={styles.label}>{label}</span>}

      <Select
        aria-label={t('segment.text.operatorLabel', { label })}
        data={OP_LABEL_KEYS.map((option) => ({
          value: option.value,
          label: t(option.labelKey),
        }))}
        value={op}
        onChange={(next) => {
          if (next === null) return;
          onChange({ op: next, value, caseSensitive });
        }}
        allowDeselect={false}
        disabled={disabled}
        comboboxProps={{ withinPortal: true }}
      />

      {showValue ? (
        <div className={styles.valueField}>
          <TextInput
            aria-label={t('segment.text.valueLabel', { label })}
            placeholder={
              op === 'regex'
                ? t('segment.text.regexPlaceholder')
                : t('segment.text.valuePlaceholder')
            }
            value={value}
            onChange={(event) => {
              onChange({
                op,
                value: event.currentTarget.value,
                caseSensitive,
              });
            }}
            disabled={disabled}
            error={regexError ? true : undefined}
          />
          {regexError ? (
            <p className={styles.error} role="alert">
              {t(regexError.key, regexError.args)}
            </p>
          ) : null}
        </div>
      ) : null}

      <Switch
        label={t('segment.text.caseSensitive')}
        checked={caseSensitive}
        onChange={(event) => {
          onChange({ op, value, caseSensitive: event.currentTarget.checked });
        }}
        disabled={disabled}
      />
    </div>
  );
}
